import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import CheckWithCircle from "../../../../../svg/CheckWithCircle";
import {
  extractContentTags,
  getMainIngredients,
  getMealItemScore,
  getScoreLevel,
} from "../../helpers/mealItemHelpers";
import {
  getInfoLine,
  getMacros,
  getMealItemState,
  getStatusTagType,
  StatusTagType,
} from "../../helpers/mealItemStateHelpers";
import { resolveMealItem } from "../../helpers/planSelectors";
import { PlanMealRootItemType } from "../../model/nutritionPlanTypes";
import MarqueeRow from "../../../../common/MarqueeRow";
import MealImage from "../shared/MealImage";
import PlanTag from "../shared/PlanTag";

type Props = {
  rootItem: PlanMealRootItemType;
  onOpen: (rootItem: PlanMealRootItemType) => void;
};

const STATUS_LABELS: Record<Exclude<StatusTagType, null>, string> = {
  skipped: "Skipped",
  swapped: "Swapped",
  extras: "Extra",
  personalized: "Personalized",
};

const IMAGE_HEIGHT = 150;
const SKIPPED_IMAGE_HEIGHT = 90;

/**
 * One meal item, ported from the mobile `NutritionMealItemComponent`: the
 * photo on top (with its status pill), then the details — title with the
 * logged check, score band and portion/prep line on one row, macros, and one
 * chip row of cuisine, diets and, for recipes, the two main ingredients — a
 * marquee that glides when the chips are wider than the card and pauses on
 * hover. A skipped
 * item collapses to photo + title. The whole card opens the details panel;
 * Phase 1 has no inline actions.
 */
const MealItemCard = ({ rootItem, onOpen }: Props) => {
  const theme: Theme = useTheme();

  const { mealItem, isExtra, isAlternative } = resolveMealItem(rootItem);
  const state = getMealItemState(mealItem);
  const statusTag = getStatusTagType(mealItem, isExtra, isAlternative);
  const score = getMealItemScore(mealItem);
  const macros = getMacros(mealItem);
  const content = mealItem.food ?? mealItem.recipe;
  const { cuisineTag, dietTags } = content
    ? extractContentTags(content)
    : { cuisineTag: null, dietTags: [] };
  const ingredients = mealItem.recipe
    ? getMainIngredients(mealItem.recipe.items, 2)
    : [];
  // Cuisine, diets, then the main ingredients, as one chip row.
  const chips = [cuisineTag, ...dietTags, ...ingredients].filter(
    (chip): chip is string => !!chip
  );

  const handleOpen = () => onOpen(rootItem);

  const statusPill = statusTag && (
    <Box sx={styles.statusTag}>
      <PlanTag kind="status" label={STATUS_LABELS[statusTag]} />
    </Box>
  );

  if (state.isSkipped) {
    return (
      <Box sx={styles.card(theme, true)} onClick={handleOpen}>
        <Box sx={styles.imageSection}>
          <MealImage
            src={state.photoUrl}
            alt={state.title}
            height={SKIPPED_IMAGE_HEIGHT}
          />
          {statusPill}
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.titleRow}>
            <Box sx={styles.title(theme)} title={state.title}>
              {state.title}
            </Box>
            {state.isLogged && (
              <Box sx={styles.check}>
                <CheckWithCircle />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.card(theme, false)} onClick={handleOpen}>
      <Box sx={styles.imageSection}>
        <MealImage
          src={state.photoUrl}
          alt={state.title}
          height={IMAGE_HEIGHT}
        />
        {statusPill}
      </Box>
      <Box sx={styles.content}>
        <Box sx={styles.titleRow}>
          <Box sx={styles.title(theme)} title={state.title}>
              {state.title}
            </Box>
          {state.isLogged && (
              <Box sx={styles.check}>
                <CheckWithCircle />
              </Box>
            )}
        </Box>
        <Box sx={styles.scoreRow}>
          {score !== null && (
            <PlanTag kind="score" level={getScoreLevel(score)} score={score} />
          )}
          <Box sx={styles.infoLine(theme)}>
            {getInfoLine(mealItem, state.calories)}
          </Box>
        </Box>
        <Box sx={styles.macros(theme)}>
          P {macros.protein}g · F {macros.fat}g · C {macros.carbs}g
        </Box>
        {chips.length > 0 && (
          <MarqueeRow>
            {chips.map((chip) => (
              <PlanTag key={chip} kind="content" label={chip} />
            ))}
          </MarqueeRow>
        )}
      </Box>
    </Box>
  );
};

export default MealItemCard;

const styles = {
  card: (theme: Theme, isSkipped: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    borderRadius: "16px",
    overflow: "hidden",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: isSkipped
      ? theme.colorTokens.N[40]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: theme.colorTokens.P[60],
    },
  }),
  imageSection: {
    position: "relative" as "relative",
    width: "100%",
  },
  statusTag: {
    position: "absolute" as "absolute",
    top: "12px",
    left: "12px",
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "flex-start",
    gap: "8px",
    padding: "16px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  check: {
    flexShrink: 0,
    display: "flex",
  },
  // One line, cut with an ellipsis; the full name is the hover title. The
  // title shrinks before the logged check beside it does.
  title: (theme: Theme) => ({
    flex: "0 1 auto",
    minWidth: 0,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  // Score pill and the portion/prep line share a row.
  scoreRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
    gap: "8px",
  },
  infoLine: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  macros: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
